import { InvaderEnemy, GalaxianEnemy, BossEnemy, FallingEnemy, KamikazeEnemy, ArcEnemy, SniperEnemy, PurpleEnemy, BaseEnemy } from './Enemy';
import { EnemyBullet, BulletHell } from './Bullet';

export class EnemyManager {
  private enemies: BaseEnemy[] = [];
  private bullets: (EnemyBullet | BulletHell)[] = [];
  private canvasWidth: number;
  private fallingEnemyTimer: number = 0;
  private fallingEnemyCooldown: number = 2.0;
  private specialEnemyTimer: number = 0;
  private specialEnemyCooldown: number = 8.0;
  private initialEnemiesCleared: boolean = false;

  constructor(canvasWidth: number, _canvasHeight: number) {
    this.canvasWidth = canvasWidth;
    this.spawnWave(1);
  }

  spawnWave(level: number, stage: number = 1): void {
    this.enemies = [];
    this.initialEnemiesCleared = false;
    
    let invaderRows: number;
    let invaderCols: number;
    let galaxianCount: number;
    let includesBoss: boolean = false;
    
    // ステージ別の敵配置
    switch (stage) {
      case 1: // ステージ1: 敵は少なめ
        invaderRows = 2;
        invaderCols = 6;
        galaxianCount = 1;
        break;
      case 2: // ステージ2: 敵はそこそこ
        invaderRows = 3;
        invaderCols = 8;
        galaxianCount = 2;
        break;
      case 3: // ステージ3: 敵は多数、猛攻撃
        invaderRows = 4;
        invaderCols = 10;
        galaxianCount = 4;
        includesBoss = true;
        break;
      case 4: // ステージ4: 紫敵3体の猛攻撃
        invaderRows = 2;
        invaderCols = 6;
        galaxianCount = 1;
        // 紫敵を3体追加
        for (let i = 0; i < 3; i++) {
          const x = 150 + i * 200;
          const y = 80;
          this.enemies.push(new PurpleEnemy(x, y));
        }
        break;
      default:
        invaderRows = Math.min(3 + Math.floor(level / 3), 5);
        invaderCols = Math.min(8 + Math.floor(level / 2), 12);
        galaxianCount = Math.min(2 + Math.floor(level / 2), 6);
        break;
    }
    
    // ステージ4以外では通常の敵を配置
    if (stage !== 4) {
      // インベーダー敵生成
      for (let row = 0; row < invaderRows; row++) {
        for (let col = 0; col < invaderCols; col++) {
          const x = 50 + col * 60;
          const y = 50 + row * 50;
          this.enemies.push(new InvaderEnemy(x, y));
        }
      }

      // ギャラクシアン敵生成
      for (let i = 0; i < galaxianCount; i++) {
        const x = 100 + i * 120;
        const y = 120;
        this.enemies.push(new GalaxianEnemy(x, y));
      }

      // ボス敵（ステージ3またはレベル5の倍数）
      if (includesBoss || level % 5 === 0) {
        this.enemies.push(new BossEnemy(this.canvasWidth / 2 - 40, 80));
      }
    }
  }

  update(deltaTime: number, playerX?: number, playerY?: number, stage: number = 1): void {
    // 初期配置敵が全滅したかチェック
    const initialEnemies = this.enemies.filter(enemy => 
      !(enemy instanceof FallingEnemy) && 
      !(enemy instanceof KamikazeEnemy) && 
      !(enemy instanceof ArcEnemy) && 
      !(enemy instanceof SniperEnemy)
    );
    
    if (initialEnemies.length === 0) {
      this.initialEnemiesCleared = true;
    }
    
    // 初期配置敵が残っている間は移動敵を生成
    if (!this.initialEnemiesCleared) {
      // ステージ3では敵の出現頻度を少し上げる（調整済み）
      const fallingCooldownMultiplier = stage === 3 ? 0.7 : 1.0;
      const specialCooldownMultiplier = stage === 3 ? 0.8 : 1.0;
      
      this.fallingEnemyTimer -= deltaTime;
      if (this.fallingEnemyTimer <= 0) {
        this.spawnFallingEnemy();
        this.fallingEnemyTimer = (this.fallingEnemyCooldown + Math.random() * 1.5) * fallingCooldownMultiplier;
      }

      this.specialEnemyTimer -= deltaTime;
      if (this.specialEnemyTimer <= 0) {
        this.spawnSpecialEnemy();
        this.specialEnemyTimer = (this.specialEnemyCooldown + Math.random() * 3.0) * specialCooldownMultiplier;
      }
    }

    this.enemies.forEach(enemy => {
      if ((enemy as any).update.length > 1) {
        (enemy as any).update(deltaTime, playerX, playerY);
      } else {
        enemy.update(deltaTime);
      }
      const newBullets = enemy.shoot();
      this.bullets.push(...newBullets);
    });

    this.bullets.forEach(bullet => bullet.update(deltaTime));

    this.enemies = this.enemies.filter(enemy => enemy.alive);
    this.bullets = this.bullets.filter(bullet => bullet.alive);
  }

  render(ctx: CanvasRenderingContext2D): void {
    this.enemies.forEach(enemy => enemy.render(ctx));
    this.bullets.forEach(bullet => bullet.render(ctx));
  }

  getEnemies(): BaseEnemy[] {
    return this.enemies;
  }

  getBullets(): (EnemyBullet | BulletHell)[] {
    const bullets = [...this.bullets];
    this.bullets = [];
    return bullets;
  }

  private spawnFallingEnemy(): void {
    const x = Math.random() * (this.canvasWidth - 30);
    const y = -30;
    this.enemies.push(new FallingEnemy(x, y));
  }

  private spawnSpecialEnemy(): void {
    const rand = Math.random();
    const x = Math.random() * (this.canvasWidth - 50);
    
    if (rand < 0.3) {
      this.enemies.push(new KamikazeEnemy(x, 50));
    } else if (rand < 0.6) {
      const fromRight = Math.random() > 0.5;
      const startX = fromRight ? this.canvasWidth - 50 : 50;
      this.enemies.push(new ArcEnemy(startX, -50, fromRight));
    } else {
      this.enemies.push(new SniperEnemy(x, -50));
    }
  }

  allEnemiesDestroyed(): boolean {
    // ステージクリアの条件：
    // 1. 初期配置敵が全滅している
    // 2. かつ、すべての敵（移動敵含む）が全滅している
    return this.initialEnemiesCleared && this.enemies.length === 0;
  }

  clear(): void {
    this.enemies = [];
    this.bullets = [];
    this.initialEnemiesCleared = false;
  }
}